// =============================================================================
// MessageComposer/useElementWidth.ts — Largura real de um elemento (F38-S15).
//
// Usado pelo MessageComposer para decidir o layout da linha a partir do
// espaço REALMENTE disponível (contêiner), não do viewport — ver
// `composerLayout.ts` para o motivo.
// =============================================================================

import * as React from 'react';

type ElementWidthObserver = {
  disconnect: () => void;
};

/**
 * Anexa um ResizeObserver a `el` e chama `onWidth` com a medição inicial
 * (síncrona) e a cada mudança de tamanho subsequente.
 *
 * Extraída do hook como função pura para ser testável sem DOM real — ver
 * `__tests__/useElementWidth.test.ts`, que injeta um `ResizeObserver` fake e
 * um elemento fake (apenas `getBoundingClientRect`).
 */
export function observeElementWidth(
  el: Pick<HTMLElement, 'getBoundingClientRect'>,
  onWidth: (width: number) => void,
): ElementWidthObserver {
  onWidth(el.getBoundingClientRect().width);

  if (typeof ResizeObserver === 'undefined') {
    return { disconnect: () => {} };
  }

  const observer = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (entry) onWidth(entry.contentRect.width);
  });
  observer.observe(el as Element);
  return { disconnect: () => observer.disconnect() };
}

/**
 * Mede a largura em pixels de um elemento via ResizeObserver e mantém o
 * valor atualizado a cada mudança de layout (ex.: abrir/fechar o painel do
 * contato, redimensionar a janela, entrar em modo mobile/sheet).
 *
 * Retorna `[setNode, width]` — `setNode` é um CALLBACK REF, não um
 * `RefObject`. Isso é proposital: um `RefObject` tem identidade estável (o
 * objeto nunca muda), então nunca poderia disparar `useEffect`/`useLayoutEffect`
 * de novo quando o elemento *desmonta e remonta* — cenário real no
 * MessageComposer, cujo `<div>` medido é substituído pelo `<AudioRecorder/>`
 * durante a gravação e volta a montar (um nó DOM NOVO) ao terminar. Um
 * callback ref é chamado pelo React a cada troca de nó, então sempre
 * reobserva o elemento certo — nunca fica preso observando um nó
 * desconectado.
 *
 * `width` é `0` antes da primeira medição — callers devem tratar isso como
 * "ainda não sei", nunca como "sem espaço".
 */
export function useElementWidth<T extends HTMLElement>(): [(node: T | null) => void, number] {
  const [width, setWidth] = React.useState(0);
  const observerRef = React.useRef<ElementWidthObserver | null>(null);

  const setNode = React.useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node) {
      setWidth(0);
      return;
    }

    observerRef.current = observeElementWidth(node, setWidth);
  }, []);

  // Desconecta ao desmontar o componente dono do hook (não só o nó medido).
  React.useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  return [setNode, width];
}
